import { Spinner } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { DetailsPopup } from "modules/common/components/form/DetailsPopup";
import { EMPTY_OBJECT_ID } from "modules/common/constants";
import { canReadOrder } from "modules/orders-manage/functionalities";
import { useMst } from "modules/root/models/hooks";
import { can, canAny } from "modules/session/auth/access";
import { EmployerDetails } from "modules/spending/employee/components/details/EmployerDetails";
import { EmployerCardTitle } from "modules/spending/employee/components/EmployerCardTitle";
import func from "modules/spending/functionalities";
import React, { useEffect, useState } from "react";

interface EmployeeCardProps {
    /** пусто — новый сотрудник */
    employerId: string;
    presetDepartmentId?: string;
    /** вакансия: новый сотрудник сразу на эту должность */
    presetPositionId?: string;
    onClose(): void;
    onChanged(): void;
}

/**
 * Карточка сотрудника поверх дерева — та же, что на странице «Сотрудники»
 * (состав полей, документы, перевод на должность), без перехода на другую страницу.
 */
export const EmployeeCard = observer(
    ({ employerId, presetDepartmentId, presetPositionId, onClose, onChanged }: EmployeeCardProps) => {
        const { employee, session, inventoryItems } = useMst();
        const [ready, setReady] = useState(false);
        const id = employerId || EMPTY_OBJECT_ID;

        useEffect(() => {
            let alive = true;
            setReady(false);

            const dictionaries: Promise<unknown>[] = [];
            if (employee.positions.isEmpty) {
                dictionaries.push(employee.positions.load());
            }
            if (employee.portfolios.isEmpty) {
                dictionaries.push(employee.portfolios.load());
            }
            // подразделения перечитываем всегда: дерево только что могло измениться
            dictionaries.push(employee.departments.load());
            if (!employee.documentNotes.length) {
                dictionaries.push(employee.loadDocumentNotes());
            }

            Promise.all([
                ...dictionaries,
                employee.details.load(id),
                employee.premiums.load(id),
                employee.loadSpendings(id),
            ])
                .catch(() => undefined)
                .finally(() => alive && setReady(true));

            return () => {
                alive = false;
            };
        }, [employee, id]);

        const canChange = can(func.EMPLOYER_EDIT, session.access);

        return (
            <DetailsPopup
                title={
                    ready ? (
                        <EmployerCardTitle
                            header={employee.details.name || "Новый сотрудник"}
                            store={employee}
                            employerId={id}
                            access={session.access}
                            materials={inventoryItems}
                            orderAccess={canReadOrder(session.access)}
                        />
                    ) : (
                        "Сотрудник"
                    )
                }
                onCancel={onClose}
                width={1310}
            >
                {ready ? (
                    <EmployerDetails
                        baseUrl={session.baseUrl}
                        positions={employee.positions}
                        employer={employee.details}
                        onSaved={() => {
                            onChanged();
                            onClose();
                        }}
                        onRemoved={() => {
                            onChanged();
                            onClose();
                        }}
                        canChange={canChange}
                        canSalary={can(func.SCHEDULE_EDIT, session.access)}
                        onPrintCredentials={employee.printCredentials}
                        spendings={employee.projects}
                        orderAccess={canReadOrder(session.access)}
                        employerList={employee.list}
                        employerId={id}
                        portfolios={employee.portfolios}
                        documentNotes={employee.sortedDocumentNotes}
                        departments={employee.departmentOptions}
                        presetDepartmentId={presetDepartmentId}
                        presetPositionId={presetPositionId}
                        canOpenPositions={canAny([func.DEPARTMENT_READ, func.DEPARTMENT_EDIT], session.access)}
                    />
                ) : (
                    <div style={{ padding: 60, display: "grid", placeItems: "center" }}>
                        <Spinner size={32} />
                    </div>
                )}
            </DetailsPopup>
        );
    }
);
